// Sidebar.js - FIXED with correct usage display
import React, { useRef, useState, useEffect } from 'react';
import './Sidebar.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import {  HelpCircle, Zap, LogOut, User } from 'lucide-react';
import { 
  getUserPresentations, 
  deletePresentation,
  updatePresentation,
  formatTimestamp,
  sharePresentation 
} from '../firestoreService';
import { usePlanTracker } from '../hooks/usePlanTracker';
import { getPlanLimits } from '../constants/planLimits';

// ✅ Memoized PresentationItem - only re-renders when ITS data changes

const PresentationItem = React.memo(({ 
  presentation,
  isEditing,
  editTitle,
  isMenuOpen,
  isSharing,
  isDeleting,
  onLoad,
  onToggleMenu,
  onShare,
  onStartRename,
  onSaveRename,
  onCancelRename,
  onRenameKeyDown,
  onDelete,
  onEditTitleChange,
  editInputRef,
  menuRef
}) => {
  return (
    <div 
      className={`recent-item ${!isEditing ? 'clickable' : ''}`}
      onClick={() => !isEditing && onLoad(presentation)}
    >
      <div className="recent-item-content">
        {isEditing ? (
          <input
            ref={editInputRef}
            type="text"
            className="recent-item-title-input"
            value={editTitle}
            onChange={(e) => onEditTitleChange(e.target.value)}
            onBlur={() => onSaveRename(presentation.id)}
            onKeyDown={(e) => onRenameKeyDown(e, presentation.id)}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div className="recent-item-title">
            {presentation.title || 'Untitled'}
          </div>
        )}
        <div className="recent-item-meta">
          <span className="recent-item-count">
            {presentation.slideCount} slides
          </span>
          <span className="recent-item-dot">•</span>
          <span className="recent-item-time">
            {formatTimestamp(presentation.createdAt)}
          </span>
        </div>
      </div>
      
      <div className="presentation-menu-wrapper" ref={isMenuOpen ? menuRef : null}>
        <button
          className="presentation-menu-btn"
          onClick={(e) => onToggleMenu(presentation.id, e)}
          title="More options"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
            <circle cx="6" cy="12" r="1.5" fill="currentColor"/>
            <circle cx="18" cy="12" r="1.5" fill="currentColor"/>
          </svg>
        </button>
        
        {isMenuOpen && (
          <div className="presentation-dropdown-menu">
            <button
              className="presentation-dropdown-item share"
              onClick={(e) => onShare(presentation, e)}
              disabled={isSharing}
            >
              {isSharing ? (
                <>
                  <svg className="share-spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                  <span>Sharing...</span>
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="18" cy="5" r="3"></circle>
                    <circle cx="6" cy="12" r="3"></circle>
                    <circle cx="18" cy="19" r="3"></circle>
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                  </svg>
                  <span>{presentation.isPublic ? 'Copy Link' : 'Share'}</span>
                </>
              )}
            </button>
            
            <button
              className="presentation-dropdown-item"
              onClick={(e) => onStartRename(presentation, e)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              <span>Rename</span>
            </button>
            
            <button
              className="presentation-dropdown-item delete"
              onClick={(e) => onDelete(presentation, e)}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <svg className="delete-spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                  <span>Deleting...</span>
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                  <span>Delete</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // ✅ Only re-render if THIS presentation's data changed
  return (
    prevProps.presentation.id === nextProps.presentation.id &&
    prevProps.presentation.title === nextProps.presentation.title &&
    prevProps.presentation.slideCount === nextProps.presentation.slideCount &&
    prevProps.isEditing === nextProps.isEditing &&
    prevProps.isMenuOpen === nextProps.isMenuOpen &&
    prevProps.isSharing === nextProps.isSharing &&
    prevProps.isDeleting === nextProps.isDeleting &&
    prevProps.editTitle === nextProps.editTitle
  );
});

function Sidebar({ onLoadPresentation, onNewChat }) {
  const { user, logout } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showUsageSubmenu, setShowUsageSubmenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  const [presentations, setPresentations] = useState([]);
  const [loadingPresentations, setLoadingPresentations] = useState(false);
  const [presentationsError, setPresentationsError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [presentationToDelete, setPresentationToDelete] = useState(null);

  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
 const navigate = useNavigate(); 
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const editInputRef = useRef(null);

  const [sharingId, setSharingId] = useState(null);

  const {
    userPlan,
    usage,
    isLoading: planLoading,
    getRemainingProjects,
    canDownloadMP4,
    shouldShowUpgradePrompt
  } = usePlanTracker(user?.uid);

  const getUserInitials = (email) => {
    if (!email) return "U";
    const name = email.split("@")[0];
    return name.charAt(0).toUpperCase();
  };

  const getPlanDisplayName = () => {
    if (planLoading) return "Loading...";
    return userPlan.charAt(0).toUpperCase() + userPlan.slice(1) + " Plan";
  };
 
  // ✅ FIXED: Show actual usage with correct limits
 // ✅ FIXED: Show remaining projects (positive focus)
  const getUsageSummary = () => {
    if (planLoading) return "";
    
    const remaining = getRemainingProjects();
    const totalRemaining = remaining.total;
    
    // Show remaining projects with positive messaging
    if (totalRemaining === 1) {
      return "1 project remaining";
    }
    return `${totalRemaining} projects remaining`;
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
        setShowUsageSubmenu(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  useEffect(() => {
    if (user) {
      fetchPresentations();
    } else {
      setPresentations([]);
    }
  }, [user]);

  const fetchPresentations = async () => {
    if (!user) return;
    
    try {
      setLoadingPresentations(true);
      setPresentationsError(null);
      
      
      const userPresentations = await getUserPresentations(user.uid);
      
      setPresentations(userPresentations);
      
      
    } catch (error) {
      
      setPresentationsError('Failed to load presentations');
    } finally {
      setLoadingPresentations(false);
    }
  };

  const handleToggleMenu = (presentationId, e) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === presentationId ? null : presentationId);
  };

  const handleSharePresentation = async (presentation, e) => {
    e.stopPropagation();
    setOpenMenuId(null);
    
    try {
      setSharingId(presentation.id);
      
      
      const shareId = await sharePresentation(
        presentation.id,
        user.uid,
        user.displayName || user.email?.split('@')[0] || 'Anonymous',
        user.email || ''
      );
      
      setPresentations(prev => 
        prev.map(p => 
          p.id === presentation.id 
            ? { ...p, shareId, isPublic: true }
            : p
        )
      );
      
      const generatedUrl = `${window.location.origin}/shared/${shareId}`;
      setShareUrl(generatedUrl);
      setShowShareModal(true);
      
     
      
    } catch (error) {
      
      alert('Failed to share presentation. Please try again.');
    } finally {
      setSharingId(null);
    }
  };

  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      const btn = document.querySelector('.share-copy-btn');
      if (btn) {
        const originalText = btn.textContent;
        btn.textContent = 'Copied!';
        setTimeout(() => {
          btn.textContent = originalText;
        }, 1500);
      }
    } catch (error) {
      
    }
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setShareUrl('');
  };

  const handleStartRename = (presentation, e) => {
    e.stopPropagation();
    setEditingId(presentation.id);
    setEditTitle(presentation.title || 'Untitled');
    setOpenMenuId(null);
  };

  const handleSaveRename = async (presentationId) => {
    if (!editTitle.trim()) {
      handleCancelRename();
      return;
    }

    try {
   
      
      await updatePresentation(presentationId, {
        title: editTitle.trim()
      });
      
      setPresentations(prev => 
        prev.map(p => 
          p.id === presentationId 
            ? { ...p, title: editTitle.trim() }
            : p
        )
      );
      
      
      setEditingId(null);
      setEditTitle('');
    } catch (error) {
     
      alert('Failed to rename presentation. Please try again.');
      handleCancelRename();
    }
  };

  const handleCancelRename = () => {
    setEditingId(null);
    setEditTitle('');
  };

  const handleRenameKeyDown = (e, presentationId) => {
    if (e.key === 'Enter') {
      handleSaveRename(presentationId);
    } else if (e.key === 'Escape') {
      handleCancelRename();
    }
  };

  const handleDeletePresentation = async (presentation, e) => {
    e.stopPropagation();
    setPresentationToDelete(presentation);
    setShowDeleteModal(true);
    setOpenMenuId(null);
  };

  const confirmDelete = async () => {
    if (!presentationToDelete) return;
    
    try {
      setDeletingId(presentationToDelete.id);
      
      
      await deletePresentation(presentationToDelete.id);
      
      setPresentations(prev => prev.filter(p => p.id !== presentationToDelete.id));
      
    
    } catch (error) {
      
      alert('Failed to delete presentation. Please try again.');
    } finally {
      setDeletingId(null);
      setShowDeleteModal(false);
      setPresentationToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setPresentationToDelete(null);
  };

  const handleLoadPresentation = (presentation) => {
    if (editingId === presentation.id) return;
    
    
    
    if (onLoadPresentation) {
      onLoadPresentation(presentation);
    }
  };

  const handleLogout = () => {
  logout();
  setShowUserMenu(false);
  window.location.href = '/'; // ← Add this line to redirect to landing page
};

  return (
    <>
      {showShareModal && (
        <div className="modal-overlay" onClick={closeShareModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Share Presentation</h3>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', color: '#a1a1aa', fontSize: '0.875rem' }}>
                Anyone with this link can view your presentation
              </p>
              <div className="share-link-container">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="share-link-input"
                  onClick={(e) => e.target.select()}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn modal-btn-cancel" onClick={closeShareModal}>
                Close
              </button>
              <button className="modal-btn modal-btn-confirm share-copy-btn" onClick={handleCopyShareLink}>
                Copy Link
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete presentation?</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete "{presentationToDelete?.title || 'Untitled'}"? This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="modal-btn modal-btn-cancel" onClick={cancelDelete}>
                Cancel
              </button>
              <button className="modal-btn modal-btn-delete" onClick={confirmDelete} disabled={deletingId}>
                {deletingId ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`left-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-top">
            {!sidebarCollapsed && (
              <div className="sidebar-logo-wrapper">
                <img src="/logo-s.png" alt="Cayoty Logo" className="sidebar-logo" />
                <h2>Cayoty</h2>
              </div>
            )}
            <button 
              className={`sidebar-toggle-btn ${sidebarCollapsed ? 'collapsed' : ''}`}
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              title={sidebarCollapsed ? "Open sidebar" : "Close sidebar"}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <line x1="9" y1="3" x2="9" y2="21"/>
              </svg>
            </button>
          </div>
        </div>
        
        <button 
          className="new-chat-btn"
          onClick={onNewChat}
          title="Start new presentation"
        >
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="16"></line>
            <line x1="8" y1="12" x2="16" y2="12"></line>
          </svg>
          {!sidebarCollapsed && <span>New Project</span>}
        </button>
        
        <div className="sidebar-content">
          <div className="sidebar-section">
            <div className="sidebar-section-header">
              <h3>Recent</h3>
              {presentations.length > 0 && (
                <button 
                  className="refresh-btn"
                  onClick={fetchPresentations}
                  disabled={loadingPresentations}
                  title="Refresh presentations"
                >
                  {loadingPresentations ? (
                    <svg className="refresh-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                    </svg>
                  )}
                </button>
              )}
            </div>
            
            <div className="recent-list">
              {loadingPresentations && (
                <div className="loading-presentations">
                  <div className="spinner-small"></div>
                  <span>Loading presentations...</span>
                </div>
              )}
              
              {presentationsError && (
                <div className="presentations-error">
                  <span>⚠️ {presentationsError}</span>
                  <button onClick={fetchPresentations}>Retry</button>
                </div>
              )}
              
              {!loadingPresentations && !presentationsError && presentations.length === 0 && (
                <div className="recent-item empty">No recent presentations</div>
              )}
              
              {!loadingPresentations && presentations.length > 0 && (
                presentations.map(presentation => (
                  <PresentationItem
                    key={presentation.id}
                    presentation={presentation}
                    isEditing={editingId === presentation.id}
                    editTitle={editTitle}
                    isMenuOpen={openMenuId === presentation.id}
                    isSharing={sharingId === presentation.id}
                    isDeleting={deletingId === presentation.id}
                    onLoad={handleLoadPresentation}
                    onToggleMenu={handleToggleMenu}
                    onShare={handleSharePresentation}
                    onStartRename={handleStartRename}
                    onSaveRename={handleSaveRename}
                    onCancelRename={handleCancelRename}
                    onRenameKeyDown={handleRenameKeyDown}
                    onDelete={handleDeletePresentation}
                    onEditTitleChange={setEditTitle}
                    editInputRef={editInputRef}
                    menuRef={menuRef}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        <div className="sidebar-user-section" ref={userMenuRef}>
          <button 
            className="sidebar-user-button"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            
<div className="sidebar-user-avatar">
  {user?.photoURL ? (
    <img src={user.photoURL} alt="User" className="sidebar-user-image" />
  ) : (
    <span className="sidebar-user-initials">
      {getUserInitials(user?.email)}
    </span>
  )}
</div>
            {!sidebarCollapsed && (
              <>
                <div className="sidebar-user-info">
                  <div className="sidebar-user-name">
                    {user?.displayName || user?.email?.split('@')[0]}
                  </div>
                  <div className="sidebar-user-plan">
                    {getPlanDisplayName()}
                  </div>
                  {!planLoading && (
                    <div 
                      className={`sidebar-user-usage ${shouldShowUpgradePrompt() ? 'warning' : ''}`}
                     title={`Prompt: ${getRemainingProjects().prompt} remaining, Audio: ${getRemainingProjects().audio} remaining`}
                    >
                      {getUsageSummary()}
                    </div>
                  )}
                </div>
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  className={`sidebar-user-arrow ${showUserMenu ? 'open' : ''}`}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </>
            )}
          </button>

          {showUserMenu && !sidebarCollapsed && (
  <div className="sidebar-user-menu">
    {/* REPLACE the theme toggle button with this Usage button */}
    <button 
      className="sidebar-user-menu-item usage-item"
      onClick={(e) => {
        e.stopPropagation();
        setShowUsageSubmenu(!showUsageSubmenu);
      }}
    >
      <Zap size={16} />
      <span>Usage</span>
      <svg 
        width="12" 
        height="12" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2"
        className={`usage-arrow ${showUsageSubmenu ? 'open' : ''}`}
        style={{ marginLeft: 'auto' }}
      >
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    </button>

    {/* Usage submenu */}
    {showUsageSubmenu && (
      <div className="usage-submenu">
        <div className="usage-submenu-item">
          <span className="usage-label">Prompt:</span>
          <span className="usage-value">{getRemainingProjects().prompt} remaining</span>
        </div>
        <div className="usage-submenu-item">
          <span className="usage-label">Audio:</span>
          <span className="usage-value">{getRemainingProjects().audio} remaining</span>
        </div>
        <div className="usage-submenu-item">
          <span className="usage-label">HD Video:</span>
           <span className="usage-value">{canDownloadMP4().remaining} remaining</span>
        </div>
      </div>
    )}

             <button 
    className="sidebar-user-menu-item"
    onClick={() => {
      navigate('/resources-help');  // ← GOOD
      setShowUserMenu(false);
    }}
  >
    <HelpCircle size={16} />
    <span>Help & Resources</span>
  </button>

  <button  
    className="sidebar-user-menu-item"
    onClick={() => {
      navigate('/profile');  // ← GOOD
      setShowUserMenu(false);
    }}
  >
    <User size={16} />
    <span>Profile</span>
  </button>

  <button 
    className={`sidebar-user-menu-item upgrade ${shouldShowUpgradePrompt() ? 'pulse' : ''}`}
    onClick={() => {
      navigate('/pricing-plans');  // ← GOOD
      setShowUserMenu(false);
    }}
  >
                <Zap size={16} />
                <span>
                  {userPlan === 'free' ? 'Upgrade Plan' : 'Change Plan'}
                </span>
              </button>

              <div className="sidebar-user-menu-divider"></div>

              <button 
                className="sidebar-user-menu-item logout"
                onClick={handleLogout}
              >
                <LogOut size={16} />
                <span>Log Out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Sidebar;